import { isArray } from 'lodash';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { ProcessTaskInputValueDiffInfo } from '../components/process-task-configuration/process-task-configuration.component';
import { CalculationValue, ConstantValue, ExpressionValue, LogicConditionTransform, TaskOutputKey, ValueTransform } from '../model/backend';
import { IUserSetting_ShakerControlProfile } from '../model/shaker-control/shaker-control-userdata-template-types';
import { IThingieCreationContext } from '../model/thingie-creation-wizard/thingie-creation-context';
import { IReassignable, ITask, ITaskInputType, IUserInput, IUserSetting } from '../model/thingie/task';
import { IThingie } from '../model/thingie/thingie';
import { ITaskOutputKey } from '../services/common/websocket-connection.service';
import { ThingieWizardContextService } from '../services/thingie-wizard-context/thingie-wizard-context.service';
import { ThingieService } from '../services/thingie/thingie-service.service';
import { deepCopySync } from './deep-copy';
import { isEqualWithIgnoringUndefinedProperties } from './equality';
import { TreeNode, walkLogicTree, walkLogicTreePair } from './logic';
import { isAllFalse } from './validators/at-least-one-true.validator';
import { isStepValid } from './validators/step.validator';


interface IStatus {
  loaded: boolean;
  completed: boolean;
  showError?: boolean;
}

export interface ITabStatus {
  [key: string]: IStatus;
}

export interface ITaskCondition {

  /**
   * Index: input key or special condition -> status
   */
  [key: string]: IStatus;
}

/**
 * This interface represents the validation status of thingies, processes and tasks in the replicates configuration step.
 */
export interface IThingieValidationStatus {
  thingieId?: string;
  statuses: {
    [processIdx: number]: {
      [taskIdx: number]: boolean;
    };
  };
}

interface ITaskValidationContext {
  processIdx?: number;
  taskIdx?: number;
}


export class ExperimentCreationValidator {
  public static async validateExperiment(contextService: ThingieWizardContextService, thingieService: ThingieService, tabsStatus: ITabStatus): Promise<ITabStatus> {
    const context = (await lastValueFrom(contextService.context$.pipe(take(1))))!;

    const baseNameFormControl = contextService.getFormControlToValidate('baseThingieName');
    const replicateCountFormControl = contextService.getFormControlToValidate('replicateCount');

    // Basic settings step validation
    if (
      context.applicationTemplate
      && context.thingieTemplate.project !== ''
      && context.thingieTemplate.name !== ''
      && (replicateCountFormControl?.valid ?? context.thingies.length > 0)
      && (baseNameFormControl?.valid || baseNameFormControl?.disabled)
    ) {
      tabsStatus.basicSettings.completed = true;
    } else {
      tabsStatus.basicSettings.completed = false;
    }

    // Check, if any thingie name is invalid
    if (context.thingieNameStates.some(s => s !== null)) {
      tabsStatus.basicSettings.completed = false;
    }

    // process structure
    const activeProcesses = context.thingieTemplate.processes.filter(process => process.removed !== true);
    tabsStatus.processStructure.completed = activeProcesses.length > 0
      && activeProcesses.some(process => process.tasks.some(task => task.removed !== true));

    // general task
    const validations: boolean[] = [];
    context.thingieTemplate.processes.forEach((p, processIdx) => {
      p.tasks.forEach((t, taskIdx) => validations.push(ExperimentCreationValidator.validateTask(
        t,
        context.thingieTemplate,
        ExperimentCreationValidator.getInputsWithValidReplicateOverrides(context, processIdx, taskIdx),
        { processIdx, taskIdx }
      )));
    });
    validations.push(await ExperimentCreationValidator.validateAllReassignableSeriesNotDisabled(context.thingieTemplate, thingieService));

    tabsStatus.generalTaskConfiguration.completed = validations.every(Boolean);


    // replicates

    // check for each thingie separately and continue
    tabsStatus.replicatesConfiguration.completed = true;
    const replicatesValidations: boolean[] = [];

    context.thingies.forEach(thingie => {
      thingie.processes.forEach((p, processIdx) => {
        p.tasks.forEach((t, taskIdx) => {
          replicatesValidations.push(ExperimentCreationValidator.validateTask(t, thingie, new Set<string>(), { processIdx, taskIdx }));
        });
      });

      tabsStatus.replicatesConfiguration.completed = replicatesValidations.every(Boolean);
    });


    // graph configuration
    tabsStatus.graphConfiguration.completed = true;

    return tabsStatus;
  }

  /**
   * Validates a thingie.
   *
   * @param thingie the thingie to validate
   * @returns whether the thingie is valid
   */
  public static validateThingie(thingie: IThingie) {
    for (const [processIdx, process] of thingie.processes.entries()) {
      for (const [taskIdx, task] of process.tasks.entries()) {
        if (!ExperimentCreationValidator.validateTask(task, thingie, new Set<string>(), { processIdx, taskIdx })) {
          return false;
        }
      }
    }
    return true;
  }


  /**
   * Validates a thingie and return an object containing the validation status of all tasks and processes.
   */
  public static getThingieValidationStatus(thingie: IThingie): IThingieValidationStatus {
    const thingieValidationStatus: IThingieValidationStatus = {
      thingieId: thingie._id,
      statuses: {}
    };

    thingie.processes.forEach((process, processIdx) => {
      process.tasks.forEach((task, taskIdx) => {
        if (!thingieValidationStatus.statuses[processIdx]) {
          thingieValidationStatus.statuses[processIdx] = {};
        }
        thingieValidationStatus.statuses[processIdx][taskIdx] = ExperimentCreationValidator.validateTask(task, thingie, new Set<string>(), { processIdx, taskIdx });
      });
    });

    return thingieValidationStatus;
  }

  /**
   * This function needs to be kept in sync with TaskConfiguration.isValid
   */
  public static validateTask(task: ITask, thingie?: IThingie, validOverrideInputs = new Set<string>(), validationContext: ITaskValidationContext = {}) {
    const userSettingsValidations: boolean[] = [];

    for (const key of Object.keys(task.inputs)) {
      let userSettingsValid = true;

      const taskInput = task.inputs[key];
      if (ExperimentCreationValidator.isHiddenTaskInput(taskInput)) {
        userSettingsValidations.push(true);
        continue;
      }
      if (validOverrideInputs.has(key)) {
        userSettingsValidations.push(true);
        continue;
      }
      if ('value' in taskInput) {
        if (taskInput.value === '' || taskInput.value === null || taskInput.value === undefined) {
          if (!('optional' in taskInput) || taskInput.optional === false) {
            userSettingsValid = false;
          }
        }

        if (taskInput.item.valueInfo[0].range) {
          if ((taskInput.value as number) < taskInput.item.valueInfo[0].range.min) {
            userSettingsValid = false;
          } else if ((taskInput.value as number) > taskInput.item.valueInfo[0].range.max) {
            userSettingsValid = false;
          }

          // check if the range contains the increment (step value)
          const stepSize = taskInput.item.valueInfo[0].range.increment;
          const minValue = taskInput.item.valueInfo[0].range.min;
          const maxValue = taskInput.item.valueInfo[0].range.max;

          // check if the step is honored (only for number inputs)
          if (
            taskInput.inputElementType === 'number'
            && stepSize
            && !isStepValid((taskInput.value as number), stepSize, minValue, maxValue)
          ) {
            userSettingsValid = false;
          }
        }

        if (taskInput.inputElementType === 'logic-builder') {
          if (taskInput.value === '' || taskInput.value === null || taskInput.value === undefined) {
            userSettingsValid = false;
          } else {
            walkLogicTree(taskInput.value as TreeNode, node => {
              if (node.type === 'placeholder') {
                const grandParent = node._parent?._parent;
                const isLastElement = isArray(node._parent) && node._key === node._parent.length - 1;
                if (grandParent !== undefined && grandParent.type === 'condition' &&
                          (grandParent.operator === 'AND' || grandParent.operator === 'OR') &&
                          isLastElement
                ) {
                  // this is okay
                } else {
                  userSettingsValid = false;
                }
              } else if (node.type === 'constant' && node.value === undefined) {
                userSettingsValid = false;
              } else if (node.type === 'series') {
                if (node.taskOutput === undefined || !ExperimentCreationValidator.isLogicBuilderSeriesAvailable(node.taskOutput, thingie, validationContext)) {
                  userSettingsValid = false;
                }
              }
            });
          }
        }

        if (taskInput.inputElementType === 'grid' && taskInput.item.valueInfo[0].vartype === 'boolean' && taskInput.optional !== true) {
          if (isAllFalse(taskInput.value)) {
            userSettingsValid = false;
          }
        }

        // check for possible conditions
        if (('condition' in taskInput)) {
          try {
            // check if the condition
            const satisfied = ExperimentCreationValidator.validateTaskInputCondition(taskInput.condition, task);

            if (!satisfied) {
              // NOT satisfied, so ignore the previous validation and set True
              userSettingsValid = true;
            }
          } catch (error) {
            // just ignore the catch
          }
        }
      } else if (!('optional' in taskInput) || taskInput.optional === false) {
        // here we check if the input is optional we will ignore it
        // but if not: then must be checked for possible values
        // if the setting is related to user "usersetting" then if the value
        // does not exist, we probably will have "condition"
        // then we go through conditions and check all of them if the conditions
        // are established, this input must have a value!

        if (('condition' in taskInput)) {
          // check if the condition is satisfied or not
          try {
            const satisfied = ExperimentCreationValidator.validateTaskInputCondition(taskInput.condition, task);
            if (satisfied === true) {
              userSettingsValid = false;
            }
          } catch (error) {
            userSettingsValid = false;
          }
        } else if ((taskInput.source as string) === 'usersetting') {
          userSettingsValid = false;
        }
      }

      userSettingsValidations.push(userSettingsValid);
    }
    const userSettingsValid = userSettingsValidations.every(Boolean);

    const reassignablesValid = Object.values(task.inputs)
      .filter((input): input is IReassignable => input.source === 'reassignable')
      .filter(input => !ExperimentCreationValidator.isHiddenTaskInput(input))
      .filter(input => input.condition === undefined || ExperimentCreationValidator.validateTaskInputCondition(input.condition, task))
      .filter(input => input.optional !== true)
      .every(input => input.value !== undefined);

    const feedingProfileValid = Object.values(task.inputs)
      .filter((i): i is IUserSetting => i.source === 'usersetting' && i.inputElementType === 'lisprofile-generator')
      .filter(input => !ExperimentCreationValidator.isHiddenTaskInput(input))
      .filter(input => !Object.entries(task.inputs).some(([key, taskInput]) => taskInput === input && validOverrideInputs.has(key)))
      .filter(input => input.condition === undefined || ExperimentCreationValidator.validateTaskInputCondition(input.condition, task))
      .every(input => ExperimentCreationValidator.hasConfiguredValue(input.value));

    const shakingProfileValid = Object.values(task.inputs)
      .filter((i): i is IUserSetting => i.source === 'usersetting' && i.inputElementType === 'shaker-control')
      .filter(input => !ExperimentCreationValidator.isHiddenTaskInput(input))
      .filter(input => !Object.entries(task.inputs).some(([key, taskInput]) => taskInput === input && validOverrideInputs.has(key)))
      .filter(input => input.condition === undefined || ExperimentCreationValidator.validateTaskInputCondition(input.condition, task))
      .every((e) => {
        const s = e as IUserSetting_ShakerControlProfile;
        if (s.templateId === undefined || s.value === undefined) {
          return false;
        }

        // Check that each selected feature has an initial value
        return s.value.features.every(feature => {
          if (s.value.initialValues.find(v => v.type === feature) === undefined) {
            return false;
          }
          return true;
        });
      });

    return reassignablesValid && feedingProfileValid && userSettingsValid && shakingProfileValid;
  }

  private static getInputsWithValidReplicateOverrides(context: IThingieCreationContext, processIdx: number, taskIdx: number) {
    const validOverrideInputs = new Set<string>();
    const templateTask = context.thingieTemplate.processes[processIdx]?.tasks[taskIdx];
    if (templateTask === undefined || context.thingies.length === 0) {
      return validOverrideInputs;
    }

    Object.entries(templateTask.inputs)
      .filter(([, input]) => 'value' in input && input.value === undefined)
      .forEach(([key, input]) => {
        if (input.source !== 'usersetting' || input.inputElementType !== 'lisprofile-generator') {
          return;
        }
        const replicateInputs = context.thingies
          .map(thingie => ExperimentCreationValidator.getTaskIgnoringDuplicatedTasks(thingie, processIdx, taskIdx)?.inputs[key])
          .filter((replicateInput): replicateInput is IUserSetting => replicateInput?.source === 'usersetting');
        if (replicateInputs.length === context.thingies.length && replicateInputs.every(replicateInput => ExperimentCreationValidator.hasConfiguredValue(replicateInput.value))) {
          validOverrideInputs.add(key);
        }
      });

    return validOverrideInputs;
  }

  private static isHiddenTaskInput(input: ITaskInputType) {
    return 'item' in input && input.item.visibility === 'hidden';
  }

  private static getTaskIgnoringDuplicatedTasks(thingie: IThingie, processIdx: number, taskIdx: number) {
    return thingie.processes[processIdx]?.tasks.filter(task => !task.duplicated)[taskIdx];
  }

  private static hasConfiguredValue(value: unknown) {
    if (value === '' || value === null || value === undefined) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  }

  private static isLogicBuilderSeriesAvailable(taskOutput: TaskOutputKey, thingie?: IThingie, validationContext: ITaskValidationContext = {}) {
    if (taskOutput.task.thingie !== undefined && taskOutput.task.thingie !== thingie?._id) {
      return true;
    }
    const processIdx = taskOutput.task.processIdx ?? validationContext.processIdx;
    const taskIdx = taskOutput.task.taskIdx ?? validationContext.taskIdx;
    if (processIdx === undefined || taskIdx === undefined) {
      return false;
    }
    const referencedTask = thingie?.processes[processIdx]?.tasks[taskIdx];
    if (referencedTask === undefined || referencedTask.removed === true) {
      return false;
    }
    return referencedTask.outputs[taskOutput.name] !== undefined;
  }

  public static validateTaskInputCondition(condition: ITaskCondition, task: ITask) {
    // all conditions listed in the object have to be satisfied
    for (const key of Object.keys(condition)) {
      if (key.startsWith('! ')) {
      // This is a special condition that is enforced when the value is changed
        continue;
      }
      const input = task.inputs[key];
      const targetValue = condition[key];
      if (!input) {
        throw new Error(`Malformed condition in template: input '${key}' does not exist`);
      }
      if (input.source !== 'usersetting' && input.source !== 'reassignable') {
        throw new Error(`Malformed condition in template: Can only check usersetting or reassignable inputs, but '${key}' is not a usersetting or reassignable`);
      }
      if (typeof(targetValue) === 'object') {
        // this might be a mongoDB-like query with operators - which is not implemented yet
        // so just check deep object equality
        if (!isEqualWithIgnoringUndefinedProperties(input.value as object, targetValue)) {
          return false;
        }
      } else {
      // check literal equality
        if (input.value !== targetValue) {
          return false;
        }
      }
    }
    // no objections
    return true;
  }

  private static async validateAllReassignableSeriesNotDisabled(thingie: IThingie, thingieService: ThingieService) {
    for (const process of thingie.processes) {
      for (const task of process.tasks) {
        for (const input of Object.values(task.inputs)) {
          if (!(await ExperimentCreationValidator.validateReassignableSeriesNotDisabled(input, thingie, thingieService))) {
            return false;
          }
        }
      }
    }
    return true;
  }

  /**
   * Check whether a series that is selected in a reassignable output is not disabled.
   *
   * Some outputs can be disabled because of user settings. For example, the biomass
   * task has 10 emission outputs, one for each wavelength. The user selects which
   * wavelengths they want using a multiselect. When the user changes their selection,
   * the respective outputs are un-disabled or disabled, which also indicates that the
   * series should or should not be shown in the series selector.
   *
   * Now when the user selects a series in a reassignable input, like in the "Target series"
   * input of PID control, they might select a series that is not disabled, but then
   * change the setting in the other task, so that the selected series becomes disabled.
   *
   * In that case, this function should return true, and a validation error on the
   * reassignable input will be shown.
   *
   * @param s the task input that is being checked
   * @param thingie the thingie that is being edited, for context
   * @returns Whether a validation error should be shown, because the series that
   *   is currently selected in the given reassignable task input is disabled.
   */
  public static async validateReassignableSeriesNotDisabled(s: ITaskInputType, thingie: IThingie | undefined | null, thingieService: ThingieService) {
    if (s.source !== 'reassignable') {
      return true;
    }
    if (s.value === undefined) {
      // handled by another validator
      return true;
    }
    const seriesKey = s.value as ITaskOutputKey;
    if (seriesKey.task.thingie !== thingie?._id) {
      if (seriesKey.task.thingie === undefined) {
        // this should have matched the _id of the given `thingie` parameter, if it is undefined... weird
        return false;
      }
      thingie = await firstValueFrom(thingieService.getThingieById(seriesKey.task.thingie));
    }

    const seriesOutput = thingie?.processes[seriesKey.task.processIdx]?.tasks[seriesKey.task.taskIdx]?.outputs[seriesKey.name];
    if (seriesOutput === undefined) {
      return false;
    }
    return seriesOutput.disabled !== true;
  }

  public static checkForIndividualTaskChangesAndPrepareTemplateThingie(context: IThingieCreationContext) {
    const changes: ProcessTaskInputValueDiffInfo = [];
    context.thingies.forEach(thingie => {
      thingie.processes.forEach((process, pIndex) => {
        process.tasks.filter(t => t.duplicated === false).forEach((task, tIndex) => {
          Object.entries(task.inputs).forEach(([key, input]) => {
            const template = (context.thingieTemplate
              .processes[pIndex]
              .tasks[tIndex]
              .inputs[key] as IUserSetting | IUserInput);

            if (template === undefined) {
              return;
            }

            // If a differing value has been detected earlier
            // we don't need to check again.
            if (changes[pIndex]?.[tIndex]?.[key] !== undefined) {
              return;
            }

            /**
             * @param a lhs
             * @param b rhs
             * @returns whether lhs and rhs represent the same series, while adjusting the lhs to skip duplicated tasks
             */
            const compareTaskOutputKeysWithoutDuplicates = (a: TaskOutputKey, b: TaskOutputKey) => {
              if (a.task.thingie !== undefined && a.task.thingie !== thingie._id) {
                // this is an external thingie - compare normally
                return isEqualWithIgnoringUndefinedProperties(a, b);
              }
              if (a.task.processIdx === undefined || a.task.taskIdx === undefined) {
                // refers to the current task - compare normally
                return isEqualWithIgnoringUndefinedProperties(a, b);
              }
              // calculate input value's task index when ignoring duplicated tasks
              const leftTask = thingie.processes[a.task.processIdx]
                ?.tasks[a.task.taskIdx];
              if (leftTask === undefined) {
                // weird
                throw new Error('Left task not found');
              }
              const leftTaskIdxWithoutDuplicates = thingie.processes[a.task.processIdx]
                ?.tasks.filter(t => !t.duplicated).findIndex(t => t === leftTask);
              if (leftTaskIdxWithoutDuplicates === -1) {
                // weird
                throw new Error('Left task lookup failed');
              }
              const leftTaskOutputWithoutDuplicates = {
                ...a,
                task: {
                  ...a.task,
                  taskIdx: leftTaskIdxWithoutDuplicates,
                }
              };
              return isEqualWithIgnoringUndefinedProperties(leftTaskOutputWithoutDuplicates, b);
            };

            let different = false;

            if (input.source === 'usersetting' && input.inputElementType === 'logic-builder' && input.value !== undefined) {

              /**
               * Special case for the logic builder, where the logic should be considered "unchanged" when the same series is
               * selected, _while ignoring duplicated tasks_. This is important for the scenario where a replicate has a duplicated
               * task, and then a new logic task is added.
               */
              const lhs: ExpressionValue = { type: 'expression', expression: input.value };
              const rhs: ExpressionValue = { type: 'expression', expression: template.value };
              let sameLogic = true;
              const sameStructure = walkLogicTreePair(lhs, rhs, (left, right) => {
                if (left.type === 'series' && right.type === 'series' && left.taskOutput !== undefined && right.taskOutput !== undefined) {
                  if (!compareTaskOutputKeysWithoutDuplicates(left.taskOutput, right.taskOutput)) {
                    sameLogic = false;
                  }
                  return;
                }
                switch (left.type) {
                  case 'calculation':
                    if (left.operator !== (right as CalculationValue).operator) {
                      sameLogic = false;
                    }
                    break;
                  case 'logicTransform':
                    if (left.transform !== (right as LogicConditionTransform).transform) {
                      sameLogic = false;
                    }
                    if (!isEqualWithIgnoringUndefinedProperties(left.settings, (right as LogicConditionTransform).settings)) {
                      sameLogic = false;
                    }
                    break;
                  case 'transform':
                    if (left.transform !== (right as ValueTransform).transform) {
                      sameLogic = false;
                    }
                    break;
                  case 'expression':
                  case 'condition':
                  case 'series':
                  case 'placeholder':
                    break;
                  case 'constant':
                    if (left.value !== (right as ConstantValue).value) {
                      sameLogic = false;
                    }
                    break;
                  default:
                    // assert that the switch is exhaustive and arg is of type `never`
                    ((_: never) => { /**/ })(left);
                }
              });
              if (!sameLogic || !sameStructure) {
                different = true;
              }
            } else if (input.source === 'usersetting' || input.source === 'userinput') {
              if (!(isEqualWithIgnoringUndefinedProperties(
                input.value,
                template.value
              ) && input.templateId === template.templateId)) {
                different = true;
              }
            } else if (input.source === 'reassignable' && input.value !== undefined) {
              if (!compareTaskOutputKeysWithoutDuplicates(input.value, template.value as TaskOutputKey)) {
                different = true;
              }
            }

            if (different) {
              if (changes[pIndex] === undefined) {
                changes[pIndex] = [];
              }

              changes[pIndex][tIndex] = {
                ...changes[pIndex][tIndex],
                [key]: {
                  value: deepCopySync(template.value),
                  templateId: template.templateId
                }
              };

              template.value = undefined;
              template.templateId = undefined;
            }
          });
        });
      });
    });

    return changes;
  }
}
